/**
 * Integration Configuration Service
 * 
 * This service follows SOLID principles to handle configuration operations
 * for integrations in a modular, extensible way.
 */

import { ComponentType } from 'react';
import { IntegrationDefinition, IntegrationConfigurationModalProps } from '../types';
import { integrationRegistry } from '../registry';

/**
 * Interface for integration configuration strategies (Strategy Pattern)
 */
export interface IIntegrationConfigurationStrategy {
  /** Check if this integration supports configuration */
  supportsConfiguration(): boolean;
  

  /** Get the configuration modal component */
  getConfigurationModal(): ComponentType<IntegrationConfigurationModalProps> | null;
  
  /** Transform integration data for configuration modal */
  transformIntegrationData(integrationData: any): any;
  
  /** Get custom actions for this integration */
  getCustomActions(): Array<{ id: string; label: string; icon?: any; description?: string; }>;
}

/**
 * Base configuration strategy implementing common behavior
 */
export class BaseConfigurationStrategy implements IIntegrationConfigurationStrategy {
  constructor(protected integrationDefinition: IntegrationDefinition) {}
  
  supportsConfiguration(): boolean {
    return this.integrationDefinition.capabilities?.supportsConfiguration ?? false;
  }
  
  getConfigurationModal(): ComponentType<IntegrationConfigurationModalProps> | null {
    return this.integrationDefinition.components.configurationModal ?? null;
  }
  
  transformIntegrationData(integrationData: any): any {
    // Default transformation - can be overridden by specific strategies
    return {
      integration_name: integrationData.name,
      binding_id: integrationData.bindingId,
      auth_config: {
        api_key: integrationData.binding?.auth_config?.api_key || `sk_gpt_binding-${integrationData.bindingId}`,
        tools: integrationData.binding?.auth_config?.tools
      },
      permissions: integrationData.binding?.permissions || {
        can_read: true,
        can_write: true,
        can_search: true,
        can_delete: false
      }
    };
  }
  
  getCustomActions(): Array<{ id: string; label: string; icon?: any; description?: string; }> {
    return this.integrationDefinition.capabilities?.customActions ?? [];
  }
}

/**
 * Bare MCP: never synthesize legacy sk_gpt_binding-* placeholders when the
 * stored binding has no plaintext key (show regenerate UI instead).
 */
export class BareMCPConfigurationStrategy extends BaseConfigurationStrategy {
  transformIntegrationData(integrationData: any): any {
    const permissions = integrationData.binding?.permissions || {
      can_read: true,
      can_write: true,
      can_search: true,
      can_delete: false,
    };

    return {
      integration_name: integrationData.name,
      binding_id: integrationData.bindingId,
      auth_config: {
        api_key: integrationData.binding?.auth_config?.api_key,
        description: integrationData.binding?.auth_config?.description,
      },
      permissions,
      rate_limits: permissions.rate_limits || { requests_per_hour: 2000 },
      max_content_length:
        integrationData.binding?.auth_config?.max_content_length ?? 50000,
    };
  }
}

/**
 * Configuration service factory (Factory Pattern)
 */
export class IntegrationConfigurationService {
  private strategies: Map<string, IIntegrationConfigurationStrategy> = new Map();
  
  constructor() {
    this.initializeStrategies();
  }
  
  /**
   * Initialize configuration strategies for all integrations
   */
  private initializeStrategies(): void {
    Object.keys(integrationRegistry).forEach(integrationId => {
      const integration = integrationRegistry[integrationId];
      const strategy =
        integrationId === 'bare_mcp'
          ? new BareMCPConfigurationStrategy(integration)
          : new BaseConfigurationStrategy(integration);
      this.strategies.set(integrationId, strategy);
    });
  }
  
  /**
   * Get configuration strategy for a specific integration
   */
  getStrategy(integrationId: string): IIntegrationConfigurationStrategy | null {
    return this.strategies.get(integrationId) ?? null;
  }
  
  /**
   * Check if an integration supports configuration
   */
  supportsConfiguration(integrationId: string): boolean {
    const strategy = this.getStrategy(integrationId);
    return strategy?.supportsConfiguration() ?? false;
  }
  
  /**
   * Get configuration modal component for an integration
   */
  getConfigurationModal(integrationId: string): ComponentType<IntegrationConfigurationModalProps> | null {
    const strategy = this.getStrategy(integrationId);
    return strategy?.getConfigurationModal() ?? null;
  }
  
  /**
   * Transform integration data for configuration modal
   */
  transformIntegrationData(integrationId: string, integrationData: any): any {
    const strategy = this.getStrategy(integrationId);
    return strategy?.transformIntegrationData(integrationData) ?? integrationData;
  }
  
  /**
   * Get custom actions for an integration
   */
  getCustomActions(integrationId: string): Array<{ id: string; label: string; icon?: any; description?: string; }> {
    const strategy = this.getStrategy(integrationId);
    return strategy?.getCustomActions() ?? [];
  }
}

// Singleton instance
export const configurationService = new IntegrationConfigurationService(); 